import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { NotePencil, Plus, Trash, X, FloppyDisk } from "@phosphor-icons/react";

export const MicroCheckEditor = () => {
  const [open, setOpen] = useState(false);
  const [qs, setQs] = useState([]);
  const [source, setSource] = useState("auto");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/briefing/questions").then((r) => { setQs(r.data.questions); setSource(r.data.source); }).catch(() => {});
  }, []);

  const upd = (i, patch) => setQs(qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updOpt = (i, oi, val) => upd(i, { options: qs[i].options.map((o, idx) => (idx === oi ? val : o)) });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/briefing/questions", { questions: qs });
      setQs(data.questions); setSource("custom");
      toast.success("Micro-check published — staff briefings now use your custom questions");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="microcheck-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2">
            <NotePencil size={22} weight="duotone" className="text-emerald-600" /> Today's micro-check questions
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid="microcheck-source">{source}</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">Edit, remove or add questions before staff check in. Saving overrides today's auto-generated set org-wide.</p>
        </div>
        <button data-testid="microcheck-toggle-editor" onClick={() => setOpen(!open)}
          className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold tap-press hover:border-slate-900">
          {open ? "Close editor" : "Edit questions"}
        </button>
      </div>
      {open && (
        <div className="mt-5 space-y-4">
          {qs.map((q, i) => (
            <div key={q.id || i} className="border border-slate-100 rounded-xl p-4 bg-slate-50/60" data-testid={`microcheck-q-${i}`}>
              <div className="flex gap-2">
                <input data-testid={`microcheck-q-text-${i}`} value={q.question} onChange={(e) => upd(i, { question: e.target.value })}
                  placeholder="Question" className="flex-1 h-11 px-3 rounded-lg border border-slate-200 text-sm font-semibold" />
                <button data-testid={`microcheck-q-remove-${i}`} onClick={() => setQs(qs.filter((_, idx) => idx !== i))}
                  className="p-2 text-slate-300 hover:text-red-500 tap-press"><Trash size={18} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" name={`ci-${i}`} checked={q.correct_index === oi}
                      onChange={() => upd(i, { correct_index: oi })} data-testid={`microcheck-q-${i}-correct-${oi}`} className="h-4 w-4" />
                    <input value={o} onChange={(e) => updOpt(i, oi, e.target.value)} data-testid={`microcheck-q-${i}-opt-${oi}`}
                      className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    {q.options.length > 2 && (
                      <button onClick={() => upd(i, { options: q.options.filter((_, x) => x !== oi), correct_index: Math.min(q.correct_index, q.options.length - 2) })}
                        className="text-slate-300 hover:text-red-500 tap-press"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={() => upd(i, { options: [...q.options, ""] })} data-testid={`microcheck-q-${i}-add-opt`}
                className="mt-2 text-xs font-bold text-slate-500 underline tap-press">+ option</button>
            </div>
          ))}
          <div className="flex gap-3 flex-wrap">
            <button data-testid="microcheck-add-question" disabled={qs.length >= 10} onClick={() => setQs([...qs, { sop_title: "Micro-check", question: "", options: ["", ""], correct_index: 0 }])}
              className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold flex items-center gap-1.5 tap-press hover:border-slate-900 disabled:opacity-40"><Plus size={14} /> Add question{qs.length >= 10 ? " (max 10)" : ""}</button>
            <button data-testid="microcheck-save" onClick={save} disabled={busy}
              className="h-11 px-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center gap-1.5 tap-press hover:bg-slate-800 disabled:opacity-50">
              <FloppyDisk size={14} /> {busy ? "Publishing…" : "Publish to staff briefings"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
